import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import YCodeThumbnail from '../components/YCodeThumbnail';
import './About.css';

const About = () => {
  const [activeTimeline, setActiveTimeline] = useState(0);
  const [stats, setStats] = useState({
    users: 0,
    courses: 0,
    learning: 0,
    satisfaction: 0
  });

  // Animate statistics
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = duration / steps;

    const targets = {
      users: 100,
      courses: 6,
      learning: 100,
      satisfaction: 100
    };

    let current = { users: 0, courses: 0, learning: 0, satisfaction: 0 };

    const timer = setInterval(() => {
      current.users = Math.min(current.users + targets.users / steps, targets.users);
      current.courses = Math.min(current.courses + targets.courses / steps, targets.courses);
      current.learning = Math.min(current.learning + targets.learning / steps, targets.learning);
      current.satisfaction = Math.min(current.satisfaction + targets.satisfaction / steps, targets.satisfaction);

      setStats({
        users: Math.floor(current.users),
        courses: Math.floor(current.courses),
        learning: Math.floor(current.learning),
        satisfaction: Math.floor(current.satisfaction)
      });

      if (current.users >= targets.users) {
        clearInterval(timer);
      }
    }, increment);

    return () => clearInterval(timer);
  }, []);

  // Auto-rotate timeline
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTimeline((prev) => (prev + 1) % 7);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const timeline = [
    { year: 'January 2024', title: 'Foundation', description: 'YCode was born with a vision to democratize coding education', icon: '🌱' },
    { year: 'March 2024', title: 'First Course', description: 'Launched our first comprehensive programming course', icon: '📚' },
    { year: 'June 2024', title: 'First Batch', description: 'Celebrated our first successful batch of graduates', icon: '🎓' },
    { year: 'January 2025', title: 'Advanced IDE', description: 'Launched our advanced online IDE with multi-language support', icon: '💻' },
    { year: 'March 2025', title: 'AI Features', description: 'Introduced AI Interview Simulator, Code Assistant, and intelligent code analysis', icon: '🤖' },
    { year: 'May 2025', title: 'Question Packages', description: 'Introduced comprehensive question packages with Ghost Coding feature', icon: '📦' },
    { year: 'August 2025', title: 'Expansion', description: 'Expanding our platform with more AI-powered features and advanced courses', icon: '🚀' }
  ];

  const values = [
    { icon: '🎯', title: 'Quality First', description: 'We never compromise on the quality of education and tools we provide' },
    { icon: '🤝', title: 'Community Driven', description: 'Built by developers, for developers, with community feedback' },
    { icon: '💡', title: 'Innovation', description: 'Constantly evolving with the latest technologies and teaching methods' },
    { icon: '🌍', title: 'Accessibility', description: 'Making programming education accessible to everyone, everywhere' },
    { icon: '🎓', title: 'Learning Focus', description: 'Designed specifically for learners with guided, hands-on approach' },
    { icon: '⚡', title: 'Fast & Reliable', description: 'Lightning-fast IDE with 99.9% uptime for seamless learning' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-20">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 animate-float"
            style={{
              width: `${Math.random() * 4 + 1}px`,
              height: `${Math.random() * 4 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 10 + 10}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <div className="text-center animate-fade-in">
          <div className="flex justify-center mb-8 transform hover:scale-110 transition-transform duration-300">
            <YCodeThumbnail size="large" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-slide-up">
            About{' '}
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
              YCode<span className="text-3xl md:text-4xl align-middle">uni</span>verse
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto animate-slide-up delay-200">
            Empowering the next generation of developers with professional-grade tools and comprehensive learning resources
          </p>
          <p className="text-lg text-orange-400 font-medium mb-12 animate-pulse-slow">
            🐛 Debugging the Road to Progress
          </p>
        </div>

        {/* Live Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-16">
          {[
            { label: 'Active Users', value: stats.users, suffix: '+', icon: '👥', color: 'teal' },
            { label: 'Courses', value: stats.courses, suffix: '+', icon: '📚', color: 'orange' },
            { label: 'Learning', value: stats.learning, suffix: '%', icon: '🎓', color: 'cyan' },
            { label: 'Satisfaction', value: stats.satisfaction, suffix: '%', icon: '⭐', color: 'purple' }
          ].map((stat, index) => (
            <div
              key={index}
              className={`bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:border-${stat.color}-400/50 transition-all duration-300 transform hover:scale-105 animate-fade-in`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="text-3xl mb-2">{stat.icon}</div>
              <div className="text-3xl font-bold text-white mb-1">
                {stat.value}{stat.suffix}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Mission */}
          <div className="bg-gradient-to-br from-teal-900/20 to-cyan-900/20 backdrop-blur-md rounded-2xl p-8 border border-teal-400/30 hover:border-teal-400/50 transition-all duration-300 transform hover:scale-105">
            <div className="text-5xl mb-4">🎯</div>
            <h2 className="text-3xl font-bold text-white mb-4">Our Mission</h2>
            <p className="text-gray-300 text-lg mb-4">
              To democratize coding education by providing professional-grade tools and resources that make learning programming accessible, engaging, and effective for everyone.
            </p>
            <p className="text-gray-300">
              We believe that everyone should have the opportunity to learn coding with the same quality tools that professionals use in the industry.
            </p>
            <div className="mt-6 flex gap-2">
              <span className="px-4 py-2 bg-teal-500/20 border border-teal-400/50 rounded-full text-teal-400 text-sm font-semibold">Accessible</span>
              <span className="px-4 py-2 bg-teal-500/20 border border-teal-400/50 rounded-full text-teal-400 text-sm font-semibold">Engaging</span>
              <span className="px-4 py-2 bg-teal-500/20 border border-teal-400/50 rounded-full text-teal-400 text-sm font-semibold">Effective</span>
            </div>
          </div>

          {/* Vision */}
          <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-md rounded-2xl p-8 border border-purple-400/30 hover:border-purple-400/50 transition-all duration-300 transform hover:scale-105">
            <div className="text-5xl mb-4">🔮</div>
            <h2 className="text-3xl font-bold text-white mb-4">Our Vision</h2>
            <p className="text-gray-300 text-lg mb-4">
              To become the world's leading platform for interactive coding education, where millions of learners can master programming and launch successful tech careers.
            </p>
            <p className="text-gray-300">
              We envision a future where anyone, anywhere can learn to code with enterprise-grade tools and AI-powered guidance.
            </p>
            <div className="mt-6 flex gap-2">
              <span className="px-4 py-2 bg-purple-500/20 border border-purple-400/50 rounded-full text-purple-400 text-sm font-semibold">Innovation</span>
              <span className="px-4 py-2 bg-purple-500/20 border border-purple-400/50 rounded-full text-purple-400 text-sm font-semibold">Leadership</span>
              <span className="px-4 py-2 bg-purple-500/20 border border-purple-400/50 rounded-full text-purple-400 text-sm font-semibold">Impact</span>
                </div>
              </div>
                </div>
              </div>

      {/* Our Journey / Timeline */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-white text-center mb-4">
          Our <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Journey</span>
        </h2>
        <p className="text-gray-400 text-center mb-16 max-w-2xl mx-auto">
          From a simple idea to a platform serving thousands of developers worldwide
        </p>
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-teal-400 via-cyan-400 to-blue-400 hidden md:block" />

          {/* Timeline Items */}
          <div className="space-y-12">
            {timeline.map((item, index) => (
              <div
                key={index}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } animate-fade-in`}
                style={{ animationDelay: `${index * 200}ms` }}
              >
                {/* Content */}
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                  <div
                    className={`bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:border-teal-400/50 transition-all duration-300 transform hover:scale-105 ${
                      activeTimeline === index ? 'border-teal-400 shadow-lg shadow-teal-400/20' : ''
                    }`}
                  >
                    <div className="text-4xl mb-3">{item.icon}</div>
                    <div className="text-teal-400 font-bold text-lg mb-2">{item.year}</div>
                    <h3 className="text-white font-semibold text-xl mb-2">{item.title}</h3>
                    <p className="text-gray-300">{item.description}</p>
                  </div>
                </div>

                {/* Center Circle */}
                <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block">
                  <div className={`w-6 h-6 rounded-full border-4 ${
                    activeTimeline === index ? 'bg-teal-400 border-teal-400 shadow-lg shadow-teal-400/50' : 'bg-gray-800 border-gray-600'
                  } transition-all duration-300`} />
                </div>

                {/* Spacer */}
                <div className="hidden md:block md:w-5/12" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Core Values */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-white text-center mb-4">
          Our <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">Values</span>
        </h2>
        <p className="text-gray-400 text-center mb-16 max-w-2xl mx-auto">
          The principles that guide everything we do
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div
              key={index}
              className="group bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 hover:border-orange-400/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl cursor-pointer"
            >
              <div className="text-5xl mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
                {value.icon}
          </div>
              <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-orange-400 transition-colors">
                {value.title}
              </h3>
            <p className="text-gray-300">
                {value.description}
            </p>
          </div>
          ))}
        </div>
      </div>

      {/* Why Us? */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-white text-center mb-16">
          Why <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Us?</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            { icon: '💻', title: 'Professional IDE', description: 'Industry-standard development environment with Monaco Editor - the same editor powering VS Code' },
            { icon: '🤖', title: 'AI Interview Simulator', description: 'Practice technical interviews with our AI interviewer. Get real-time feedback and improve your interview skills' },
            { icon: '🧠', title: 'AI Code Assistant', description: 'Get instant help with our AI assistant that understands your code and provides intelligent suggestions' },
            { icon: '🔍', title: 'AI Code Smell Detector', description: 'Automatically detect 15+ code smells with severity ratings and actionable improvement suggestions' },
            { icon: '🎓', title: 'Structured Learning', description: 'Carefully crafted curriculum from beginner to advanced with hands-on projects and real-world applications' },
            { icon: '⚡', title: 'Real-time Execution', description: 'Run code instantly in multiple languages with blazing-fast compilation and execution' },
            { icon: '👻', title: 'Ghost Coding', description: 'Watch keystroke-by-keystroke replays of expert solutions and learn problem-solving patterns' },
            { icon: '🌐', title: 'Global Community', description: 'Connect with developers from 25+ countries and learn together in our thriving community' }
          ].map((feature, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 hover:border-teal-400/50 transition-all duration-300 transform hover:scale-105"
            >
              <div className="flex items-start gap-4">
                <div className="text-5xl flex-shrink-0">{feature.icon}</div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-300">{feature.description}</p>
                </div>
            </div>
          </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="relative bg-gradient-to-r from-teal-900/20 to-cyan-900/20 backdrop-blur-md rounded-2xl p-12 text-center border border-teal-400/30 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 via-cyan-500/10 to-blue-500/10 animate-gradient-x" />
          
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Join Our Community?
          </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Start your coding journey with YCode today and become part of a global community of developers
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
                to="/courses"
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 transform hover:scale-105 inline-block shadow-lg hover:shadow-2xl"
            >
                Browse Courses
            </Link>
            <Link
                to="/ide"
                className="bg-white/10 border-2 border-white/30 hover:bg-white/20 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 transform hover:scale-105 inline-block backdrop-blur-md"
            >
                Try Our IDE
            </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black/20 border-t border-white/20 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
              <YCodeThumbnail size="medium" />
            </div>
              <p className="text-gray-400 text-sm mb-2">
              Advanced coding platform for modern learners
            </p>
              <p className="text-orange-400 text-xs">
                🐛 Debugging the Road to Progress
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Learn</h3>
              <ul className="space-y-2">
                <li><Link to="/courses" className="text-gray-400 hover:text-teal-400 transition-colors">Courses</Link></li>
                <li><Link to="/question-packages" className="text-gray-400 hover:text-teal-400 transition-colors">Practice</Link></li>
                <li><Link to="/articles" className="text-gray-400 hover:text-teal-400 transition-colors">Articles</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Tools</h3>
              <ul className="space-y-2">
                <li><Link to="/ide" className="text-gray-400 hover:text-teal-400 transition-colors">Online IDE</Link></li>
                <li><Link to="/ai-assistant" className="text-gray-400 hover:text-teal-400 transition-colors">AI Assistant</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-teal-400 transition-colors">About Us</Link></li>
                <li><Link to="/" className="text-gray-400 hover:text-teal-400 transition-colors">Home</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2024 YCode. All rights reserved. Made with ❤️ for developers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About; 

